import { useCallback, useMemo, useState } from 'react';
import { DateTime } from 'luxon';

/**
 * Date navigation state and derived day list for the schedule view.
 *
 * @param {string} homeTz - IANA timezone name, used for "today" anchoring.
 * @returns {{
 *   anchorDate: DateTime,
 *   setAnchorDate: (d: DateTime) => void,
 *   viewModeId: 'day' | 'three',
 *   setViewModeId: (id: string) => void,
 *   dayCount: number,
 *   days: DateTime[],
 *   titleRange: string,
 *   goPrev: () => void,
 *   goNext: () => void,
 *   goToday: () => void,
 * }}
 */
export function useDateNav(homeTz, language) {
  const [anchorDate, setAnchorDate] = useState(() =>
    DateTime.now().setZone(homeTz).startOf('day')
  );
  const [viewModeId, setViewModeId] = useState('day');

  const dayCount = viewModeId === 'three' ? 3 : 1;

  // Apply the active i18n language so weekday/month names render localized.
  // We re-base on every render — DateTime is immutable, this is cheap, and
  // it keeps every consumer (titleRange, days[], DayCard headers) in sync
  // when the user flips the language picker.
  const localized = useMemo(
    () => (language ? anchorDate.setLocale(language) : anchorDate),
    [anchorDate, language]
  );

  const days = useMemo(
    () => Array.from({ length: dayCount }, (_, i) => localized.plus({ days: i })),
    [localized, dayCount]
  );

  const titleRange = dayCount === 1
    ? localized.toFormat('cccc, MMMM d, yyyy')
    : `${localized.toFormat('MMM d')}  →  ${localized.plus({ days: dayCount - 1 }).toFormat('MMM d, yyyy')}`;

  const goPrev = useCallback(
    () => setAnchorDate(d => d.minus({ days: dayCount })),
    [dayCount]
  );
  const goNext = useCallback(
    () => setAnchorDate(d => d.plus({ days: dayCount })),
    [dayCount]
  );
  const goToday = useCallback(
    () => setAnchorDate(DateTime.now().setZone(homeTz).startOf('day')),
    [homeTz]
  );

  return {
    anchorDate, setAnchorDate,
    viewModeId, setViewModeId,
    dayCount, days,
    titleRange,
    goPrev, goNext, goToday,
  };
}
